/**
 * Enhanced JSON Path Parser
 * Supports array indexing and complex object navigation
 */

/**
 * Extracts a value from an object using a JSON path that supports:
 * - Dot notation: data.user.name
 * - Array indexing: results[0].matchCount
 * - Mixed notation: data.users[0].profile.name
 * - Bracket notation: data['user-info'][0]['first-name']
 */
export function getValueByPath(obj: any, path: string): any {
  if (!obj || !path) return undefined;
  
  try {
    // Normalize the path to handle different syntaxes
    const normalizedPath = normalizePath(path);
    
    // Split path into segments
    const segments = parsePathSegments(normalizedPath);
    
    // Traverse the object following the path
    let current = obj;
    for (const segment of segments) {
      if (current === null || current === undefined) {
        return undefined;
      }
      
      // Handle array access
      if (segment.isArrayAccess) {
        if (!Array.isArray(current) || segment.index === undefined) {
          return undefined;
        }
        current = current[segment.index];
      } else {
        // Handle object property access
        if (segment.key === undefined) {
          return undefined;
        }
        current = current[segment.key];
      }
    }
    
    return current;
  } catch (error) {
    console.warn('JSON path extraction error:', error);
    return undefined;
  }
}

/**
 * Normalizes different path syntaxes to a consistent format
 */
function normalizePath(path: string): string {
  // Remove leading/trailing whitespace
  path = path.trim();
  
  // Handle JSONPath expressions that start with $
  if (path.startsWith('$.')) {
    path = path.substring(2);
  } else if (path === '$') {
    return '';
  }
  
  return path;
}

/**
 * Parses a normalized path into segments
 */
function parsePathSegments(path: string): PathSegment[] {
  if (!path) return [];
  
  const segments: PathSegment[] = [];
  let current = '';
  let inBrackets = false;
  let bracketContent = '';
  
  for (let i = 0; i < path.length; i++) {
    const char = path[i];
    
    if (char === '[' && !inBrackets) {
      // Start of bracket notation
      if (current) {
        segments.push({ key: current, isArrayAccess: false });
        current = '';
      }
      inBrackets = true;
      bracketContent = '';
    } else if (char === ']' && inBrackets) {
      // End of bracket notation
      inBrackets = false;
      
      // Check if bracket content is a number (array index)
      const trimmed = bracketContent.trim();
      if (/^\d+$/.test(trimmed)) {
        segments.push({ 
          index: parseInt(trimmed, 10), 
          isArrayAccess: true 
        });
      } else {
        // String key in brackets (handle quoted strings)
        let key = trimmed;
        if ((key.startsWith("'") && key.endsWith("'")) || 
            (key.startsWith('"') && key.endsWith('"'))) {
          key = key.slice(1, -1);
        }
        segments.push({ key, isArrayAccess: false });
      }
      bracketContent = '';
    } else if (inBrackets) {
      // Inside brackets, collect content
      bracketContent += char;
    } else if (char === '.') {
      // Dot separator
      if (current) {
        segments.push({ key: current, isArrayAccess: false });
        current = '';
      }
    } else {
      // Regular character
      current += char;
    }
  }
  
  // Add final segment if exists
  if (current) {
    segments.push({ key: current, isArrayAccess: false });
  }
  
  return segments;
}

/**
 * Represents a segment in a JSON path
 */
interface PathSegment {
  key?: string;
  index?: number;
  isArrayAccess: boolean;
}

/**
 * Validates if a JSON path syntax is supported
 */
export function isValidJsonPath(path: string): boolean {
  if (!path || typeof path !== 'string') return false;
  
  try {
    normalizePath(path);
    parsePathSegments(normalizePath(path));
    return true;
  } catch {
    return false;
  }
}

/**
 * Converts a path to a human-readable description
 */
export function describeJsonPath(path: string): string {
  if (!path) return 'Root object';
  
  const normalized = normalizePath(path);
  const segments = parsePathSegments(normalized);
  
  if (segments.length === 0) return 'Root object';
  
  const descriptions = segments.map(segment => {
    if (segment.isArrayAccess) {
      return `element at index ${segment.index}`;
    } else {
      return `property "${segment.key}"`;
    }
  });
  
  return descriptions.join(' → ');
}

/**
 * Suggests valid array index paths for an object
 */
export function suggestArrayPaths(obj: any, maxDepth: number = 3): string[] {
  const suggestions: string[] = [];
  
  function traverse(current: any, path: string, depth: number) {
    if (depth > maxDepth || !current) return;
    
    if (Array.isArray(current)) {
      // Suggest first element access
      if (current.length > 0) {
        const arrayPath = path ? `${path}[0]` : '[0]';
        suggestions.push(arrayPath);
        
        // Continue traversing the first element
        traverse(current[0], arrayPath, depth + 1);
      }
    } else if (typeof current === 'object') {
      // Traverse object properties
      Object.keys(current).forEach(key => {
        const keyPath = path ? `${path}.${key}` : key;
        suggestions.push(keyPath);
        traverse(current[key], keyPath, depth + 1);
      });
    }
  }
  
  traverse(obj, '', 0);
  return suggestions;
} 